// ======================================================================
// File: src/modules/ipe/engineering/mom/MOMOperationsTable.js
// Purpose:
//   Display MOM operations
//   - Allow editing run time / setup time
//   - Add Process (NEW)
//   - Add Machine Group + Skill (NEW)
// ======================================================================

import React, { useState, useEffect } from "react";
import ipeEnquiryService from "../../services/ipeEnquiryService";
import MachineGroupSelectorModal from "./MachineGroupSelectorModal";
import SkillSelectorModal from "./SkillSelectorModal";
export default function MOMOperationsTable({ operations = [], reload, momId }) {

  const uniqueOperations = Array.from(
    new Map(operations.map(op => [op.id, op])).values()
  );

  // ---------------- ADD PROCESS STATE ----------------
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    operation_id: "",
    sequence_no: "",
    predecessor_id: "",
    run_time: "",
    setup_time: "",
    remarks: "",
  });

  const [showMGModal, setShowMGModal] = useState(false);

  const [operationsMaster, setOperationsMaster] = useState([]);
  
  function handleMGSelect(data) {
    setSelectedMachineGroups(data.machineGroups);
    setPrimaryMachineGroup(data.primary);
  }
  function handleSkillSelect(data) {
    setSelectedSkills(data.skills);
    setPrimarySkill(data.primary);
  }

  // ---------------- NEW: MG + SKILL STATE ----------------
  const [selectedMachineGroups, setSelectedMachineGroups] = useState([]);
  const [primaryMachineGroup, setPrimaryMachineGroup] = useState(null);
  const [selectedSkills, setSelectedSkills] = useState([]);

  const [showSkillModal, setShowSkillModal] = useState(false);
  const [primarySkill, setPrimarySkill] = useState(null);

  // ---------------- EDIT STATE ----------------
const [editingRowId, setEditingRowId] = useState(null);
const [editForm, setEditForm] = useState({});
const [editMachineGroups, setEditMachineGroups] = useState([]);
const [editPrimaryMachineGroup, setEditPrimaryMachineGroup] = useState(null);
const [editSkills, setEditSkills] = useState([]);
const [editPrimarySkill, setEditPrimarySkill] = useState(null);

const [showEditMGModal, setShowEditMGModal] = useState(false);
const [showEditSkillModal, setShowEditSkillModal] = useState(false);

  useEffect(() => {
      loadOperations();
    }, []);

    async function loadOperations() {
      try {
        const data = await ipeEnquiryService.getOperations();
        console.log("OPERATIONS API:", data); // 👈 ADD THIS
        setOperationsMaster(data);
      } catch (e) {
        console.error("Failed to load operations", e);
      }
    }
  // ---------------- HELPERS ----------------
  const operationOptions = operationsMaster.map(op => ({
      id: op.id,
      name: op.operation_name
  }));

  const predecessorOptions = uniqueOperations.map(op => ({
    id: op.id,
    label: `${op.sequence_no} - ${op.operation_name}`
  }));

  // ---------------- UPDATE (UNCHANGED) ----------------
  async function update(lineId, field, value) {

    try {

      const payload = {};
      payload[field] = Number(value);

      await ipeEnquiryService.updateMOMLine(lineId, payload);

      reload();

    } catch (e) {

      console.error("Failed to update MOM line", e);
      alert("Update failed");

    }

  }

  // ---------------- CREATE WITH MG + SKILL ----------------
  async function handleAddProcess() {

    if (!form.operation_id) {
      alert("Process required");
      return;
    }
 
    
    if (selectedMachineGroups.length === 0) {
      alert("At least one machine group required");
      return;
    }

    //const momId = uniqueOperations[0]?.mom_id;

    if (!momId) {
      alert("MOM ID not found");
      return;
    }

    setSaving(true);

    try {

      // 1️⃣ CREATE MOM LINE
      const res = await ipeEnquiryService.createMOMLine({
        mom_id: momId,
        operation_id: Number(form.operation_id),
        sequence_no: Number(form.sequence_no || uniqueOperations.length + 1),
        predecessor_id: form.predecessor_id || null,
        run_time: Number(form.run_time || 0),
        setup_time: Number(form.setup_time || 0),
        remarks: form.remarks || "",
      });

      const lineId = res?.line_id;

      if (!lineId) {
        throw new Error("Line ID not returned from API");
      }

      // 2️⃣ SAVE MG + SKILL
      await ipeEnquiryService.saveProcessResourceMapping({
        mom_process_id: lineId,
        machine_groups: selectedMachineGroups.map(id => ({
          id,
          is_primary: id === primaryMachineGroup
        })),
        skills: selectedSkills.map(s => ({
          id: s.id
          
        }))
      });

      // RESET
      setForm({
        operation_id: "",
        sequence_no: "",
        predecessor_id: "",
        run_time: "",
        setup_time: "",
        remarks: "",
      });

      setSelectedMachineGroups([]);
      setPrimaryMachineGroup(null);
      setSelectedSkills([]);

      setAdding(false);
      reload();

    } catch (e) {

      console.error(e);
      alert("Failed to add process");

    } finally {

      setSaving(false);

    }
  }
  
  // ---------------- EDIT FLOW ----------------
async function startEdit(op) {

    setEditingRowId(op.id);

    // ✅ Reset first (important)
    setEditMachineGroups([]);
    setEditSkills([]);

    setEditForm({
      sequence_no: op.sequence_no,
      run_time: op.run_time,
      setup_time: op.setup_time,
      remarks: op.remarks || ""
    });

    try {
      const res = await ipeEnquiryService.getProcessResourceMapping(op.id);

      console.log("Mapping:", res); // ✅ debug

      setEditMachineGroups(res.machine_groups.map(m => m.machine_group_id));

      setEditPrimaryMachineGroup(
        res.machine_groups.find(m => m.is_primary)?.machine_group_id
      );

      setEditSkills(res.skills.map(s => ({ id: s.skill_id })));

    } catch (e) {
      console.error("Mapping load failed", e);
    }
  }
  function handleEditMGSelect(data) {
    setEditMachineGroups(data.machineGroups);
    setEditPrimaryMachineGroup(data.primary);
  }

  function handleEditSkillSelect(data) {
    setEditSkills(data.skills);
    setEditPrimarySkill(data.primary);
  }

  async function handleEditSave(opId) {

    try {

      await ipeEnquiryService.updateMOMLine(opId, {
        sequence_no: Number(editForm.sequence_no),
        run_time: Number(editForm.run_time),
        setup_time: Number(editForm.setup_time),
        remarks: editForm.remarks
      });

      await ipeEnquiryService.saveProcessResourceMapping({
        mom_process_id: opId,
        machine_groups: editMachineGroups.map(id => ({
          id,
          is_primary: id === editPrimaryMachineGroup
        })),
        skills: editSkills.map(s => ({
          id: s.id
        }))
      });

      setEditingRowId(null);
      reload();

    } catch (e) {
      console.error(e);
      alert("Failed to update process");
    }
  }

  function handleEditCancel() {
    setEditingRowId(null);
  }
  return (

    <div>

      {/* ================= ADD PROCESS ================= */}

      <div className="mb-2">

        {!adding && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setAdding(true)}
          >
            + Add Process
          </button>
        )}

        {adding && (

          <div className="border rounded p-2 mt-2">

            <div className="row g-2">

              {/* Seq */}
              <div className="col-md-1">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Seq"
                  value={form.sequence_no}
                  onChange={(e) =>
                    setForm({ ...form, sequence_no: e.target.value })
                  }
                />
              </div>

              {/* Process */}
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={form.operation_id}
                  onChange={(e) =>
                    setForm({ ...form, operation_id: e.target.value })
                  }
                >
                  <option value="">Select Process</option>
                  {operationOptions.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Predecessor */}
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={form.predecessor_id}
                  onChange={(e) =>
                    setForm({ ...form, predecessor_id: e.target.value })
                  }
                >
                  <option value="">None</option>
                  {predecessorOptions.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Machine Group */}
              <div className="col-md-2">
                <button
                  className="btn btn-sm btn-outline-primary w-100"
                  onClick={() => setShowMGModal(true)}
                >
                  {selectedMachineGroups.length
                    ? `MG (${selectedMachineGroups.length})`
                    : "Select MG"}
                </button>
              </div>

              {/* Skill */}
              <div className="col-md-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setShowSkillModal(true)}
                >
                  {selectedSkills.length > 0
                    ? `Skill (${selectedSkills.length})`
                    : "Select Skill"}
                </button>
              </div>

              {/* Run */}
              <div className="col-md-1">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Run"
                  value={form.run_time}
                  onChange={(e) =>
                    setForm({ ...form, run_time: e.target.value })
                  }
                />
              </div>

              {/* Setup */}
              <div className="col-md-1">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  placeholder="Setup"
                  value={form.setup_time}
                  onChange={(e) =>
                    setForm({ ...form, setup_time: e.target.value })
                  }
                />
              </div>

              {/* Actions */}
              <div className="col-md-2 d-flex gap-1">

                <button
                  className="btn btn-sm btn-success"
                  onClick={handleAddProcess}
                  disabled={saving}
                >
                  Save
                </button>

                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setAdding(false)}
                >
                  Cancel
                </button>

              </div>

            </div>

          </div>

        )}

      </div>

      {/* ================= TABLE ================= */}

      <table className="table table-bordered table-sm">

        <thead className="table-light">

          <tr>
            <th>Seq</th>
            <th>Process</th>
            <th>Machine Group</th>
            <th>Skill</th>
            <th>Run Time</th>
            <th>Setup Time</th>
            <th>Remarks</th>
            <th>Action</th>
          </tr>

        </thead>

        <tbody>

          {uniqueOperations.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center text-muted">
                No operations defined
              </td>
            </tr>
          )}

          
          {uniqueOperations.map((op) => {

            const isEditing = editingRowId === op.id;

            return (

              <tr key={op.id}>

                {/* Seq */}
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.sequence_no}
                      onChange={(e) =>
                        setEditForm({ ...editForm, sequence_no: e.target.value })
                      }
                    />
                  ) : (
                    op.sequence_no
                  )}
                </td>

                {/* Process */}
                <td>
                  {op.operation_name || `#${op.operation_id}`}
                </td>

                {/* Machine Group */}
                <td>
                  {isEditing ? (
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowEditMGModal(true)}
                    >
                      MG ({editMachineGroups.length})
                    </button>
                  ) : (
                    op.machine_group_name || "-"
                  )}
                </td>
                {/* Skill */}
                <td>
                  {isEditing ? (
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowEditSkillModal(true)}
                    >
                      Skill ({editSkills.length})
                    </button>
                  ) : (
                    op.skill_name || "-"   // ✅ FIX
                  )}
                </td>

                {/* Run Time */}
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.run_time}
                      onChange={(e) =>
                        setEditForm({ ...editForm, run_time: e.target.value })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      defaultValue={op.run_time}
                      onBlur={(e) =>
                        update(op.id, "run_time", e.target.value)
                      }
                    />
                  )}
                </td>

                {/* Setup Time */}
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.setup_time}
                      onChange={(e) =>
                        setEditForm({ ...editForm, setup_time: e.target.value })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      defaultValue={op.setup_time}
                      onBlur={(e) =>
                        update(op.id, "setup_time", e.target.value)
                      }
                    />
                  )}
                </td>

                {/* Remarks */}
                <td>
                  {isEditing ? (
                    <input
                      className="form-control form-control-sm"
                      value={editForm.remarks || ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, remarks: e.target.value })
                      }
                    />
                  ) : (
                    op.remarks || "-"
                  )}
                </td>

                {/* Action */}
                <td>
                  {isEditing ? (
                    <>
                      <button
                        className="btn btn-success btn-sm me-1"
                        onClick={() => handleEditSave(op.id)}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleEditCancel}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => startEdit(op)}
                    >
                      Edit
                    </button>
                  )}
                </td>

              </tr>

            );

          })}

        </tbody>

      </table>
          {/* ================= MACHINE GROUP MODAL ================= */}

        <MachineGroupSelectorModal
          show={showMGModal}
          onClose={() => setShowMGModal(false)}
          onSelect={handleMGSelect}
          selected={selectedMachineGroups}
          primary={primaryMachineGroup}
        />

        <SkillSelectorModal
          show={showSkillModal}
          onClose={() => setShowSkillModal(false)}
          onSelect={handleSkillSelect}
          selected={selectedSkills}
          primary={primarySkill}
        />
        {/* ================= EDIT MACHINE GROUP MODAL ================= */}
        <MachineGroupSelectorModal
          show={showEditMGModal}
          onClose={() => setShowEditMGModal(false)}
          onSelect={handleEditMGSelect}
          selected={editMachineGroups}
          primary={editPrimaryMachineGroup}
        />

        {/* ================= EDIT SKILL MODAL ================= */}
        <SkillSelectorModal
          show={showEditSkillModal}
          onClose={() => setShowEditSkillModal(false)}
          onSelect={handleEditSkillSelect}
          selected={editSkills}
          primary={editPrimarySkill}
        />
    </div>

  );

}
